import { RoleServiceResponseModel, Role, CreateRoleServiceModel, UpdateRoleServiceModel } from '../types';
import { detectFormChanges } from './changeDetector';

/**
 * Maps API role response to UI role model
 */
export const mapApiRoleToUi = (apiRole: RoleServiceResponseModel): Role => {
  // Show all tenant names, joined with commas
  const tenantNames = apiRole.tenants && apiRole.tenants.length > 0 
    ? apiRole.tenants.map(t => t.name).join(', ')
    : 'No Tenant';
    
  // Extract tenant IDs for form population
  const tenantIds = apiRole.tenants && apiRole.tenants.length > 0 
    ? apiRole.tenants.map(t => t.id)
    : [];
    
  return {
    id: apiRole.id,
    roleId: apiRole.id, // Using the same ID for now
    name: apiRole.name,
    description: apiRole.description,
    tenant: tenantNames,
    tenantIds: tenantIds, // Array of tenant IDs for form population
    totalUsers: apiRole.totalUserCount || 0, // Map totalUserCount from API response
    status: apiRole.isActive ? 'Active' : 'Inactive',
    isDefault: apiRole.isDefault || false, // Map isDefault property from API response
  };
};

/**
 * Maps UI role form data to API create model
 */
export const mapUiToCreateRoleApiModel = (formData: {
  name: string;
  description: string;
  tenant: string[];
  permissions?: string[];
}): CreateRoleServiceModel => {
  return {
    name: formData.name,
    description: formData.description,
    permissionIds: formData.permissions || [],
    tenantIds: formData.tenant.filter(Boolean),
  };
};

/**
 * Maps UI role form data to API update model
 */
export const mapUiToUpdateRoleApiModel = (formData: {
  name: string;
  description: string;
  status: 'Active' | 'Inactive';
}): UpdateRoleServiceModel => {
  // Map status string to boolean
  const getIsActive = (status: string): boolean => {
    return status === 'Active';
  };

  return {
    name: formData.name,
    description: formData.description,
    isActive: getIsActive(formData.status),
  };
};

/**
 * Maps UI role form data to API update model with only changed fields for PATCH requests
 * Note: PATCH endpoint only accepts name, description, and isActive fields
 * Tenant assignments are managed separately via /roles/{id}/tenants endpoint
 */
export const mapUiToUpdateRoleApiModelPatch = (
  originalRole: Role,
  formData: {
    name: string;
    description: string;
    status: 'Active' | 'Inactive';
  }
): UpdateRoleServiceModel => {
  // Map status string to boolean
  const getIsActive = (status: string): boolean => {
    return status === 'Active';
  };

  // Create the full update object (API model) - only include fields accepted by PATCH endpoint
  const fullUpdate: UpdateRoleServiceModel = {
    name: formData.name,
    description: formData.description,
    isActive: getIsActive(formData.status),
  };

  // Create the original object for comparison (also API model) - only include fields accepted by PATCH endpoint
  const originalApiModel: UpdateRoleServiceModel = {
    name: originalRole.name,
    description: originalRole.description,
    isActive: getIsActive(originalRole.status),
  };

  console.log('🔐 Role update mapping:', {
    original: originalApiModel,
    updated: fullUpdate,
    formData
  });

  // Return only the changed fields by comparing API models
  const changes = detectFormChanges(originalApiModel, fullUpdate);
  console.log('🔐 Detected changes:', changes);
  
  return changes;
};
